import { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import SignatureCanvas from "react-signature-canvas";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { toast } from "sonner";
import { Car, PenTool, Trash2, ArrowRight, CheckCircle } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SignaturePage = () => {
  const navigate = useNavigate();
  const { dossierId } = useParams();
  const sigCanvas = useRef(null);
  const canvasContainerRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);
  const [dossier, setDossier] = useState(null);
  const [isDrawing, setIsDrawing] = useState(false);

  useEffect(() => {
    const fetchDossier = async () => {
      try {
        const response = await axios.get(`${API}/dossiers/${dossierId}`);
        setDossier(response.data);
      } catch (error) {
        toast.error("Dossier non trouvé");
        navigate('/');
      }
    };
    fetchDossier();
  }, [dossierId, navigate]);

  // Prevent scroll when drawing on canvas (mobile fix)
  // This blocks ALL scroll on the page while drawing
  useEffect(() => {
    const preventScroll = (e) => {
      if (isDrawing) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    // Block scroll on both container and document
    if (isDrawing) {
      document.body.style.overflow = 'hidden';
      document.body.style.touchAction = 'none';
    } else {
      document.body.style.overflow = '';
      document.body.style.touchAction = '';
    }

    document.addEventListener('touchmove', preventScroll, { passive: false });
    return () => {
      document.removeEventListener('touchmove', preventScroll);
      document.body.style.overflow = '';
      document.body.style.touchAction = '';
    };
  }, [isDrawing]);

  const clearSignature = () => {
    sigCanvas.current.clear();
    setHasSignature(false);
  };

  const handleBegin = () => {
    setIsDrawing(true);
  };

  const handleEnd = () => {
    setIsDrawing(false);
    if (sigCanvas.current && !sigCanvas.current.isEmpty()) {
      setHasSignature(true);
    }
  };

  const saveSignature = async () => {
    if (sigCanvas.current.isEmpty()) {
      toast.error("Veuillez signer avant de continuer");
      return;
    }

    setLoading(true);
    try {
      const signatureData = sigCanvas.current.toDataURL('image/png');
      
      await axios.put(`${API}/dossiers/${dossierId}/signature`, {
        signature: signatureData
      });
      
      toast.success("Signature enregistrée !");
      navigate(`/documents/${dossierId}`);
    } catch (error) {
      toast.error("Erreur lors de l'enregistrement de la signature");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#000091] rounded-lg flex items-center justify-center">
              <Car className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="font-bold text-[#000091]">Carte Grise Étrangère</h1>
              <p className="text-xs text-slate-500">Signature électronique</p>
            </div>
          </div>
        </div>
        <div className="h-1 flex">
          <div className="flex-1 bg-[#002395]"></div>
          <div className="flex-1 bg-white"></div>
          <div className="flex-1 bg-[#ED2939]"></div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Progress indicator */}
        <div className="flex items-center justify-center gap-2 mb-8">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-green-500 text-white flex items-center justify-center">
              <CheckCircle className="w-5 h-5" />
            </div>
            <span className="text-sm text-slate-500">Formulaire</span>
          </div>
          <div className="w-16 h-0.5 bg-[#000091]"></div>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-[#000091] text-white flex items-center justify-center text-sm font-medium">
              2
            </div>
            <span className="text-sm text-[#000091] font-medium">Signature</span>
          </div>
          <div className="w-16 h-0.5 bg-slate-200"></div>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-slate-200 text-slate-500 flex items-center justify-center text-sm font-medium">
              3
            </div>
            <span className="text-sm text-slate-500">Documents</span>
          </div>
        </div>

        {dossier && (
          <div className="bg-[#000091]/5 border border-[#000091]/20 rounded-lg p-4 mb-8">
            <p className="text-sm text-[#000091]">
              <span className="font-semibold">Dossier:</span>{' '}
              {dossier.client.prenom} {dossier.client.nom} - {dossier.vehicle.marque} {dossier.vehicle.modele}
            </p>
          </div>
        )}

        <Card className="border-slate-200" data-testid="signature-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-[#0F172A]">
              <PenTool className="w-5 h-5 text-[#000091]" />
              Signez votre demande
            </CardTitle>
            <CardDescription>
              Votre signature sera apposée sur les formulaires CERFA 13757 et 13750. 
              Signez dans la zone ci-dessous avec votre doigt ou stylet.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Signature area */}
            <div className="relative" ref={canvasContainerRef}>
              <div 
                className="border-2 border-dashed border-slate-300 rounded-lg bg-white overflow-hidden touch-none"
                style={{ touchAction: 'none' }}
              >
                <SignatureCanvas
                  ref={sigCanvas}
                  canvasProps={{
                    className: 'signature-canvas w-full',
                    style: { width: '100%', height: '350px', minHeight: '350px', touchAction: 'none' }
                  }}
                  backgroundColor="white"
                  penColor="#0F172A"
                  onBegin={handleBegin}
                  onEnd={handleEnd}
                  data-testid="signature-canvas"
                />
              </div>
              
              {!hasSignature && (
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <p className="text-slate-400 text-sm">Signez ici</p>
                </div>
              )}
            </div>

            {/* Info text */}
            <div className="bg-slate-50 rounded-lg p-4">
              <p className="text-sm text-slate-600">
                <strong>Important:</strong> En signant, vous certifiez l'exactitude des informations 
                fournies et vous engagez à fournir des documents authentiques.
              </p>
            </div>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                variant="outline"
                onClick={() => navigate(-1)}
                className="border-slate-300"
                data-testid="back-btn"
              >
                ← Retour
              </Button>
              
              <Button
                variant="outline"
                onClick={clearSignature}
                className="border-slate-300"
                data-testid="clear-signature-btn"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Effacer
              </Button>
              
              <Button
                onClick={saveSignature}
                disabled={loading || !hasSignature}
                className="flex-1 bg-[#000091] hover:bg-[#000091]/90"
                data-testid="save-signature-btn"
              >
                {loading ? "Enregistrement..." : "Valider et continuer"}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default SignaturePage;
